import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { Skeleton } from '../../../../shared/ui/Working';
import Root from '../views/Root';
import Error, { InShellError } from '../views/Error';
import Home from '../views/Home';
import { Page } from '../components/Page';
import { useTranslation } from '../i18n/useTranslation';

// Direct-URL entry is a first-class case (Stripe deep-links straight into
// /professional/payment-setup/*), so the lazy chunks need something to show
// instead of a blank frame. Named so it isn't an anonymous frame in DevTools.
//
// ONE SHELL. This is the HydrateFallback of the first LAZY route (AuthLayout),
// not of the root: React Router renders every match above the first lazy one
// and puts the fallback in its place, so Root's AppShell is already mounted
// around this page and the arriving route replaces only the Outlet. When it was
// the root's fallback it brought its own AppShell, and the real one replaced
// it, header and footer included: a control focused while the chunks loaded
// lost focus to <body> (known issue in
// docs/testing/release-v1.0.0-rc.1-2026-10-06.md; tests/e2e/app-shell-focus.spec.js).
function RouteLoading() {
  const { t } = useTranslation();
  // The title says it; the bar shows it is moving (D4: no text-only wait).
  return (
    <Page title={t("common.loading")}>
      <span aria-hidden="true">
        <Skeleton h={6} r={3} />
      </span>
    </Page>
  );
}

// Route paths are product/API contracts shared with backend and mobile:
// - /login and /auth/action are claimed by the app.* AASA (Universal Links)
// - /professional/payment-setup/{refresh,complete} are Stripe Connect
//   refresh_url/return_url minted by the backend (setupStripeConnect.ts)
// Do not rename without coordinating with both teams. Any change here must be
// mirrored in docs/architecture/routes.md and scripts/smoke-test.sh, or
// `npm run quality:contracts` fails.
//
// Providers are mounted per-route through lazily loaded layouts rather than
// around the whole app: `/` only redirects to marketing, so it should not
// download the Firebase SDK, Apollo, or App Check's reCAPTCHA script. Nesting
// is AuthLayout (identity) -> DataLayout (authenticated GraphQL); a route
// placed in the wrong subtree will throw at render, which router/index.test.tsx
// guards against.
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Root />,
    // Renders INSTEAD of Root (a URL nothing matches, or Root itself
    // failing), so it brings its own AppShell; see views/Error.
    errorElement: <Error />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        // Static, beside `lazy`: the fallback is needed before the chunk is.
        HydrateFallback: RouteLoading,
        // ONE SHELL for failures too, and static for the same reason: a lazy
        // chunk that fails to load (this layout's, DataLayout's or any view's,
        // on the first load or a later navigation) or a render error below
        // here renders in Root's Outlet, so the header and footer on screen
        // stay the same nodes (router/index.test.tsx). Same stale-chunk and
        // 404 states as the root's; an unmatched URL is still the root's.
        errorElement: <InShellError />,
        lazy: async () => ({
          Component: (await import('../layouts/AuthLayout')).default,
        }),
        children: [
          {
            path: '/login',
            lazy: async () => ({
              Component: (await import('../views/Login')).default,
            }),
          },
          {
            path: '/auth/action',
            lazy: async () => ({
              Component: (await import('../views/AuthAction')).default,
            }),
          },
          {
            // The screen that ASKS for the email; /auth/action handles the
            // link the email contains. Not claimed by mobile's association
            // files, so this stays a browser page — see routes.md. It sits
            // under AuthLayout and NOT under DataLayout: requesting a reset
            // is a signed-out act, and mounting the authenticated GraphQL
            // client here would make the screen unreachable for exactly the
            // person who needs it.
            path: '/auth/reset',
            lazy: async () => ({
              Component: (await import('../views/ResetRequest')).default,
            }),
          },
          {
            lazy: async () => ({
              Component: (await import('../layouts/DataLayout')).default,
            }),
            children: [
              {
                path: '/professional/payment-setup',
                lazy: async () => ({
                  Component: (await import('../views/PaymentSetup')).PaymentSetupStart,
                }),
              },
              {
                path: '/professional/payment-setup/refresh',
                lazy: async () => ({
                  Component: (await import('../views/PaymentSetup')).PaymentSetupRefresh,
                }),
              },
              {
                path: '/professional/payment-setup/complete',
                lazy: async () => ({
                  Component: (await import('../views/PaymentSetup')).PaymentSetupComplete,
                }),
              },
              {
                // Three entry points share this path: the partner approval
                // email, plus Stripe's refresh_url (?step=stripe&refresh=true)
                // and return_url (?step=complete) minted by the backend.
                path: '/partner/onboarding',
                lazy: async () => ({
                  Component: (await import('../views/PartnerOnboarding')).default,
                }),
              },
              {
                path: '/partner/dashboard',
                lazy: async () => ({
                  Component: (await import('../views/PartnerDashboard')).default,
                }),
              },
              {
                path: '/admin/partners/:applicationId',
                lazy: async () => ({
                  Component: (await import('../views/AdminPartnerApplication')).default,
                }),
              },
            ],
          },
        ],
      },
    ],
  },
];

export default createBrowserRouter(routes);
